"use client";

import { Core } from "@faire/design-tokens";
import { getIsoToCountryNames } from "@faire/web/common/consts/ISO_TO_COUNTRY_NAME";
import { useStrictLocalization } from "@faire/web/common/localization";
import {
  countryCallingCodeOptions,
  ICountryCodeOption,
  parsePhoneNumber,
} from "@faire/web/common/phoneNumber";
import { Chevron } from "@faire/web/slate/icons/foundation/Chevron";
import {
  FormInputLabel,
  inputBorderStyles,
  inputStylesNoBorder,
} from "@faire/web/slate/Input";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import ClickOutside from "@faire/web/ui/ClickOutside";
import { ReadOnlyText } from "@faire/web/ui/Scaffolding/ReadOnlyText";
import { ISO3166Alpha3 } from "@faire/web-api/indigofair/iso3166/ISO3166Alpha3";
import { NationalNumber } from "libphonenumber-js";
import omit from "lodash/omit";
// eslint-disable-next-line @faire-react/no-restricted-imports
import uniqueId from "lodash/uniqueId";
import { observer } from "mobx-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  NumberFormatBase,
  NumberFormatBaseProps,
  NumberFormatValues,
  usePatternFormat,
} from "react-number-format";
import { FormatInputValueFunction } from "react-number-format/types/types";
import styled, { useTheme } from "styled-components";

import { getDerivedPropsFromErrorProps, IErrorProps } from "../error";

const CountryCodeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border: none;
  appearance: none;
  outline: none;
  flex-basis: 64px;
  border-right: 1px solid ${Core.border.muted};
  background-color: ${Core.surface.inverse};

  &:disabled {
    background-color: ${Core.surface.muted};
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid -webkit-focus-ring-color;
    z-index: 1;
  }
`;

const Border = styled(Row)<{ $showErrorState: boolean }>`
  ${inputBorderStyles};
  min-height: 42px;
  &:focus,
  &:focus-within {
    ${CountryCodeButton} {
      border-right: 1px solid ${Core.border.subdued};
    }
  }
`;

const CountryCodeDropdownWrapper = styled.div`
  position: relative;
`;

const NumberFormat = styled(NumberFormatBase)<{
  disabled?: boolean;
}>`
  ${inputStylesNoBorder};
  flex: 1;
`;

const CountryCodeDropdown = styled.ul`
  height: 230px;
  position: absolute;
  left: 0;
  top: 0;
  border: 1px solid ${Core.border.muted};
  border-top: 0;
  overflow-y: auto;
  background: ${Core.surface.inverse};
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 0;
  z-index: 1;
`;

const CountryCallingCodeOptionButton = styled.button<{ $highlight?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: left;
  width: 100%;
  padding: 10px 0;
  cursor: pointer;
  background: ${(props) => (props.$highlight ? Core.surface.subdued : "none")};

  &:hover {
    background: ${Core.surface.subdued};
  }
`;

const CountryCallingCodeOptionLabel = styled(Typography)`
  flex: 1;
`;

const optionDataId = (option: ICountryCodeOption) =>
  `${option.label}${option.countryCallingCode}`;

export interface IProps
  extends Omit<NumberFormatBaseProps, "onChange" | "format" | "customInput">,
    IErrorProps {
  id?: string;
  defaultPhoneNumber?: string;
  defaultCountry?: keyof typeof ISO3166Alpha3;
  countryCodeOptions?: ICountryCodeOption[];
  label?: React.ReactNode;
  onChangePhoneNumber?: (value: string) => void;
  format?: string;
  helper?: string;
  "data-test-id"?: string;
  "aria-label"?: string;
  readonly?: boolean;
  sideCta?: React.ReactNode;
}

// eslint-disable-next-line complexity
const PhoneInputBase: React.FC<IProps> = (props) => {
  const theme = useTheme();
  const { strictLocalize } = useStrictLocalization();

  const { onChangePhoneNumber, defaultPhoneNumber, defaultCountry } = props;

  const id = props.id ?? uniqueId("phone-input-");

  const countryCodeOptions =
    props.countryCodeOptions ?? countryCallingCodeOptions(strictLocalize);

  const getCountryCallOptionIndexByLabel = useCallback(
    (label: string) => {
      const index = countryCodeOptions.findIndex(
        (option) => option.label === label
      );
      return Math.max(index, 0);
    },
    [countryCodeOptions]
  );

  const isoToCountryNames = useMemo(
    () => getIsoToCountryNames(strictLocalize),
    [strictLocalize]
  );

  const getCountryCallOptionIndexByISO = useCallback(
    (countryIso?: string) => {
      if (!countryIso) {
        return 0;
      }
      return getCountryCallOptionIndexByLabel(
        isoToCountryNames[countryIso as keyof typeof isoToCountryNames] ?? ""
      );
    },
    [getCountryCallOptionIndexByLabel, strictLocalize, isoToCountryNames]
  );

  const [selectedOptionIndex, setSelectedOptionIndex] = useState<
    number | undefined
  >(
    defaultCountry ? getCountryCallOptionIndexByISO(defaultCountry) : undefined
  );

  const defaultCountryRef = useRef(defaultCountry);

  useEffect(() => {
    if (defaultCountry !== defaultCountryRef.current) {
      setSelectedOptionIndex(getCountryCallOptionIndexByISO(defaultCountry));
      defaultCountryRef.current = defaultCountry;
    }
  }, [defaultCountry, getCountryCallOptionIndexByISO]);

  const selectedOption = countryCodeOptions[selectedOptionIndex ?? 0];

  const parsedDefaultPhoneNumber = useRef(
    defaultPhoneNumber
      ? parsePhoneNumber(defaultPhoneNumber, selectedOption, strictLocalize)
      : undefined
  );

  const [phoneNumber, setPhoneNumber] = useState<string | NationalNumber>(
    parsedDefaultPhoneNumber.current?.nationalPhoneNumber ?? ""
  );

  // This useEffect is used to set the default country code when the component is first rendered.
  // It will also send a onChangePhoneNumber when there is a defaultPhoneNumber
  useEffect(() => {
    if (parsedDefaultPhoneNumber.current) {
      const countryCallOption = getCountryCallOptionIndexByLabel(
        parsedDefaultPhoneNumber.current.countryCallingCodeOption.label
      );
      setSelectedOptionIndex(countryCallOption);
      onChangePhoneNumber?.(
        `${parsedDefaultPhoneNumber.current.countryCallingCodeOption.countryCallingCode} ${parsedDefaultPhoneNumber.current.nationalPhoneNumber}`
      );
      parsedDefaultPhoneNumber.current = undefined;
    }
  }, [getCountryCallOptionIndexByLabel, onChangePhoneNumber]);

  const pastedNationalPhoneNumber = useRef<string | NationalNumber>("");

  const pastedCountryCallingCodeOption = useRef<ICountryCodeOption>({
    label: "United States of America",
    countryCallingCode: "+1",
    format: "(###)-###-####",
  });

  const [showingDropdown, setShowingDropdown] = useState(false);

  const inputContainerRef = useRef<HTMLDivElement>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const triggerOnChange = (params: {
    updatedPhoneNumber?: string | NationalNumber;
    updatedCountryCallingCode?: string;
  }) => {
    let phoneNumberWithCode: string | NationalNumber =
      params.updatedPhoneNumber ?? phoneNumber;
    if (params.updatedCountryCallingCode) {
      phoneNumberWithCode = `${params.updatedCountryCallingCode} ${phoneNumberWithCode}`;
    } else if (selectedOption?.countryCallingCode) {
      phoneNumberWithCode = `${selectedOption.countryCallingCode} ${phoneNumberWithCode}`;
    }
    onChangePhoneNumber?.(phoneNumberWithCode as string);
  };

  const handleChange = (numberFormatValues: NumberFormatValues) => {
    if (pastedNationalPhoneNumber.current !== "") {
      setSelectedOptionIndex(
        getCountryCallOptionIndexByLabel(
          pastedCountryCallingCodeOption.current.label
        )
      );
      setPhoneNumber(pastedNationalPhoneNumber.current);
      triggerOnChange({
        updatedCountryCallingCode:
          pastedCountryCallingCodeOption.current.countryCallingCode,
        updatedPhoneNumber: pastedNationalPhoneNumber.current,
      });
      pastedNationalPhoneNumber.current = "";
    } else {
      setPhoneNumber(numberFormatValues.value);
      triggerOnChange({
        updatedPhoneNumber: numberFormatValues.value,
      });
    }
  };

  const handleClickOutside = () => {
    setShowingDropdown(false);
  };

  const focusInputElement = () => {
    if (!inputContainerRef.current) {
      return;
    }

    const inputEl = inputContainerRef.current.querySelector("input");
    if (inputEl) {
      inputEl.focus();
    }
  };

  const ensureSelectedOptionIsVisible = () => {
    if (!dropdownRef.current || selectedOption === undefined) {
      return;
    }
    const optionEl = dropdownRef.current.querySelector(
      `[data-id="${optionDataId(selectedOption)}"]`
    );
    // scrollIntoView is undefined in unit tests
    optionEl?.scrollIntoView?.({ block: "center" });
  };

  const selectCountryCode = (selectedOptionIndex: number) => {
    setSelectedOptionIndex(selectedOptionIndex);
    setShowingDropdown(false);
    triggerOnChange({
      updatedCountryCallingCode:
        countryCodeOptions[selectedOptionIndex]?.countryCallingCode,
    });
    focusInputElement();
  };

  const handleCountryCallingCodeButtonClicked = () => {
    setShowingDropdown(!showingDropdown);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (selectedOptionIndex === undefined) {
      return;
    }

    switch (e.key) {
      case "ArrowUp":
        setSelectedOptionIndex(Math.max(selectedOptionIndex - 1, 0));
        ensureSelectedOptionIsVisible();
        // prevent screen from scrolling up
        e.preventDefault();
        break;
      case "ArrowDown":
        setSelectedOptionIndex(
          Math.min(selectedOptionIndex + 1, countryCodeOptions.length - 1)
        );
        ensureSelectedOptionIsVisible();
        // prevent screen from scrolling down
        e.preventDefault();
        break;
      case "Enter":
        // prevent the button's onClick from firing
        e.preventDefault();
        setShowingDropdown(false);
        break;
      default:
        setShowingDropdown(false);
    }
  };

  const handlePaste: React.ClipboardEventHandler = (event) => {
    const pastedPhoneNumber = event.clipboardData.getData("Text");
    const { nationalPhoneNumber, countryCallingCodeOption } = parsePhoneNumber(
      pastedPhoneNumber,
      selectedOption,
      strictLocalize
    );
    pastedCountryCallingCodeOption.current = countryCallingCodeOption;
    pastedNationalPhoneNumber.current = nationalPhoneNumber;
  };

  const {
    error,
    label,
    helper,
    format,
    "aria-label": ariaLabel,
    ...rest
  } = props;

  const phoneNumberFormat = selectedOption?.format ?? format;

  const { format: patternFormat } = usePatternFormat({
    mask: "_",
    format: phoneNumberFormat ?? "",
  });

  const _format: FormatInputValueFunction = (inputValue: string) => {
    if (phoneNumberFormat && patternFormat) {
      return patternFormat(inputValue);
    }
    return inputValue;
  };

  const inputProps = omit(rest, [
    "onChangePhoneNumber",
    "defaultPhoneNumber",
    "defaultCountry",
    "countryCodeOptions",
    "strictLocalize",
  ]);

  const { showErrorState, showErrorMessage, errorMessage } =
    getDerivedPropsFromErrorProps({ error });

  const inputId = id ?? id;
  const labelId = label ? `${inputId}__label` : undefined;

  if (props.readonly) {
    return (
      <ReadOnlyText
        label={labelId}
        value={
          selectedOption?.countryCallingCode
            ? selectedOption?.countryCallingCode + " " + phoneNumber
            : (countryCodeOptions[0]?.countryCallingCode ?? "") +
              " " +
              phoneNumber
        }
      />
    );
  }

  return (
    <ClickOutside onClickOutside={handleClickOutside} onKeyDown={handleKeyDown}>
      <Row justify="space-between">
        <FormInputLabel
          id={labelId}
          $typography={{
            ...(theme.input.label ?? {}),
            color: showErrorState
              ? Core.text.critical
              : theme.input.label?.color ?? "faireBlack",
          }}
          htmlFor={inputId}
        >
          {label}
        </FormInputLabel>
        {props.sideCta ?? null}
      </Row>
      <Border $showErrorState={showErrorState} ref={inputContainerRef}>
        <CountryCodeButton
          type="button"
          onClick={handleCountryCallingCodeButtonClicked}
          data-test-id="countryCallingCodeButton"
          disabled={rest.disabled}
        >
          <Typography
            variant="paragraphSansRegular"
            truncate
            color={rest.disabled ? Core.text.disabled : Core.text.default}
          >
            {selectedOption?.countryCallingCode ??
              countryCodeOptions[0]?.countryCallingCode}
          </Typography>
          <Spacer size={4} />
          <Chevron
            direction="down"
            color={Core.icon.default}
            fontSize="8px"
            strokeWidth={2}
          />
        </CountryCodeButton>
        <NumberFormat
          id={inputId}
          type="tel"
          autoComplete="tel"
          {...inputProps}
          value={phoneNumber as string}
          format={_format}
          onPaste={handlePaste}
          onValueChange={handleChange}
          {...(ariaLabel ? { "aria-label": ariaLabel } : undefined)}
          {...(!ariaLabel && labelId
            ? { "aria-labelledby": labelId }
            : undefined)}
        />
      </Border>
      <CountryCodeDropdownWrapper ref={dropdownRef}>
        {!props.disabled && showingDropdown && (
          <CountryCodeDropdown data-test-id="countryCallingCodeDropdown">
            {countryCodeOptions.map((option, index) => (
              <li key={optionDataId(option)}>
                <CountryCallingCodeOptionButton
                  type="button"
                  tabIndex={-1}
                  onClick={() => selectCountryCode(index)}
                  $highlight={option === selectedOption}
                  data-id={optionDataId(option)}
                  data-test-id={`countryCallingCode${option.countryCallingCode}`}
                >
                  <Spacer size={12} />
                  <CountryCallingCodeOptionLabel>
                    {option.label}
                  </CountryCallingCodeOptionLabel>
                  <Spacer size={12} />
                  <Typography
                    variant="paragraphSansRegular"
                    color={Core.text.default}
                  >
                    {option.countryCallingCode}
                  </Typography>
                  <Spacer size={12} />
                </CountryCallingCodeOptionButton>
              </li>
            ))}
          </CountryCodeDropdown>
        )}
      </CountryCodeDropdownWrapper>
      {showErrorMessage ? (
        <>
          <Spacer size={4} />
          <Typography variant="labelSansRegular" color={Core.text.critical}>
            {errorMessage}
          </Typography>
        </>
      ) : null}
      {!showErrorMessage && helper ? (
        <>
          <Spacer size={4} />
          <Typography
            variant="labelSansRegular"
            color={theme.input.helper?.color ?? Core.text.subdued}
          >
            {helper}
          </Typography>
        </>
      ) : null}
    </ClickOutside>
  );
};

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/component/input/web/phone-input)
 */
export const PhoneInput = observer(PhoneInputBase);
